<template>
  <div :class="{ 'clean-editor__disabled': disabled }">
    <div v-if="showCommands">
      <slot :set-content="handleSetContent"></slot>
    </div>
    <div v-if="editor">
      <bubble-menu
        class="clean-editor__menu"
        :tippy-options="{ duration: 100 }"
        :editor="editor"
      >
        <button
          :class="{ 'is-active': editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
        >
          <WIcon icon="icBold" />
        </button>
        <button
          :class="{ 'is-active': editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <WIcon icon="icItalic" />
        </button>
        <button
          :class="{ 'is-active': editor.isActive('strike') }"
          @click="editor.chain().focus().toggleStrike().run()"
        >
          <s>S</s>
        </button>
        <button
          :class="{ 'is-active': editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
        >
          <WIcon icon="icBullets" />
        </button>
        <button
          :class="{ 'is-active': editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
        >
          <WIcon icon="icNumbering" />
        </button>
        <!-- enable link 
                <button
                    @click="handleSetLink"
                    :class="{ 'is-active': editor.isActive('link') }"
                >
                    <WIcon icon="icLink" />
                </button>
                -->
      </bubble-menu>
    </div>

    <editor-content
      :editor="editor"
      class="clean-editor"
      data-testid="markdown-editor"
    />
  </div>
</template>

<script>
import { WIcon } from '@alice-health/wonderland-vue'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import { BubbleMenu, Editor, EditorContent } from '@tiptap/vue-3'
import { PluginKey } from 'prosemirror-state'
import { Markdown } from 'tiptap-markdown'

export default {
  components: {
    EditorContent,
    BubbleMenu,
    WIcon
  },
  props: {
    value: {
      type: String,
      default: undefined
    },
    isFocused: {
      type: Boolean
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean
    },
    readonly: {
      type: Boolean
    }
  },
  emits: ['input', 'onToggleCommands'],
  data() {
    return {
      editor: null,
      showCommands: false
    }
  },
  computed: {
    isDisabled() {
      return this.disabled || this.readonly
    }
  },
  watch: {
    isFocused(value) {
      if (value) {
        this.editor.commands.focus('end')
      }
    },
    value(value) {
      if (value !== this.editor.storage.markdown.getMarkdown()) {
        this.editor.commands.setContent(value)
      }
    },
    isDisabled() {
      this.editor.setEditable(!this.isDisabled)
    }
  },
  mounted() {
    this.editor = new Editor({
      content: this.value,
      editable: !this.isDisabled,
      onUpdate: ({ editor, transaction }) => {
        if (transaction.docChanged) {
          this.$emit('input', editor.storage.markdown.getMarkdown())
        }
      },
      extensions: [
        StarterKit,
        Markdown.configure({
          html: true, // Allow HTML input/output
          tightLists: true, // No <p> inside <li> in markdown output
          tightListClass: 'tight', // Add class to <ul> that allows you to remove <p> margins when tight
          bulletListMarker: '-', // <li> prefix in markdown output
          linkify: true, // Create links from "https://..." text
          breaks: false, // New lines (\n) in markdown input are converted to <br>
          transformPastedText: true, // Allow to paste markdown text in the editor
          transformCopiedText: true // Copied text is transformed to markdown
        }),
        Link.configure({
          openOnClick: false
        }),
        Placeholder.configure({
          placeholder: this.placeholder
        }),
        Mention.extend({
          name: 'add-commands'
        }).configure({
          HTMLAttributes: {
            class: 'add-commands'
          },
          suggestion: {
            pluginKey: new PluginKey('add-commands'),
            char: '/',
            allowSpaces: false,
            render: () => {
              return {
                onStart: () => {
                  this.showCommands = true
                  this.$emit('onToggleCommands', this.showCommands)
                },
                onExit: () => {
                  this.showCommands = false
                  this.$emit('onToggleCommands', this.showCommands)
                }
              }
            }
          }
        })
      ]
    })
    if (this.isFocused) {
      this.editor.commands.focus('end')
    }
  },
  beforeUnmount() {
    this.editor?.destroy()
  },
  methods: {
    handleSetLink() {
      const previousUrl = this.editor.getAttributes('link').href
      // eslint-disable-next-line no-alert
      const url = window.prompt('URL', previousUrl)

      // cancelled
      if (url === null) {
        return
      }

      // empty
      if (url === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()

        return
      }

      // update link
      this.editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run()
    },

    handleSetContent(content) {
      const cursor = this.editor.state.selection.anchor
      this.editor.commands.deleteRange({ from: cursor - 1, to: cursor })
      this.editor.commands.insertContentAt(cursor - 1, content + ' ', {
        updateSelection: true,
        parseOptions: {
          preserveWhitespace: 'full'
        }
      })
      this.editor.commands.focus('end')
      this.$emit('input', this.editor.storage.markdown.getMarkdown())
    },
    handleClosePopup() {
      /** Enable parent to close commands **/
      this.showCommands = false
      this.editor.commands.focus('end')
    }
  }
}
</script>

<style lang="scss">
.clean-editor {
  color: var(--sys-color-content-primary);

  .ProseMirror {
    outline: none;
    > * + * {
      margin-top: 0.75em;
    }
    ul,
    ol {
      padding: 0 1rem;
      list-style: unset;
    }
    ol {
      list-style-type: decimal;
    }
    ul {
      list-style-type: disc;
    }
    p.is-editor-empty:first-child::before {
      color: var(--sys-color-content-placeholder);
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }
  }
  .ProseMirror[contenteditable='true'] {
    h1 {
      font-size: 2em;
    }
  }
  .ProseMirror[contenteditable='false'] {
    //Readonly style
    padding: 0;
    > * + * {
      margin-top: 0;
    }
    h1,
    h2,
    h3,
    h4 {
      font-size: var(--gl-font-size-xs);
    }
  }
  *:focus-visible {
    outline-color: transparent;
  }
  &__menu {
    display: flex;
    background-color: var(--gl-color-shades-white);
    border: var(--gl-border-width-xs) solid var(--gl-color-shades-gray-20);
    box-shadow: var(--gl-shadow-level-2);
    padding: var(--gl-spacing-02) var(--gl-spacing-03);
    gap: var(--gl-spacing-04);
    border-radius: var(--gl-border-radius-sm);
    button {
      padding: 0;
      border: none;
      background: none;
      color: var(--gl-color-shades-gray-100);
      font-weight: var(--gl-font-weight-regular);
      font-size: var(--gl-font-size-xs);
      line-height: var(--gl-line-height-xxs);
      opacity: 1;
      &:hover,
      &.is-active {
        color: var(--gl-color-shades-magenta-60);
      }
    }
  }
  a {
    background-color: var(--gl-color-shades-magenta-00);
    color: var(--sys-color-content-primary);
    text-decoration: none;
    border-radius: var(--gl-border-radius-xs);
    padding: 0 var(--gl-spacing-01);
    pointer-events: none;
  }
  &__disabled {
    background-color: var(--gl-color-shades-white);
    opacity: 0.3;
    cursor: not-allowed;
    height: 100%;
    pointer-events: none;
  }
}
</style>
